import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Table from "../ogComponent/table";
import Spinner from "../ogComponent/spinner";
import Modal from "../ogComponent/modal";
import axios from "axios";
import { Toast } from 'primereact/toast';

const SelfLeaveTable = () => {
  const [leaveData, setLeaveData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [balanceLeave, setBalanceLeave] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [modalValues, setModalValues] = useState({
    leave_duration: null,
    leave_type: null,
    start_date: '',
    end_date: '',
    reason: '',
    days_difference: 0
  });
  const navigate = useNavigate();
  const userId = localStorage.getItem("userId");
  const toast = useRef(null);
  const token = localStorage.getItem("accessToken");

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // setSearchParams({ page: newPage });
  };

  useEffect(() => {
    const fetchBalanceLeave = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API}/api/leavemanagement/balance-leave/${userId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        setBalanceLeave(response.data.balance_leave);
      } catch (error) {
        console.error('Error fetching balance leave:', error);
      }
    };

    fetchBalanceLeave();
  }, [userId, token]);

  const header = [
    { name: "Si No", label: "#", active: true },
    { name: "start_date", label: "Start Date", active: true },
    { name: "end_date", label: "End Date", active: true },
    { name: "LeaveType", label: "Leave Type", active: true },
    { name: "Days", label: "Days", active: true },
    { name: "DayType", label: "Day Type", active: true },
    { name: "Status", label: "Status", active: true },
  ];

  useEffect(() => {
    const fetchLeaveData = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API}/api/leavemanagement/leaves/employee/${userId}/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const formattedData = response.data.map((item, index) => ({
          "Si No": index + 1,
          LeaveType: item?.leave_type || "N/A",
          "Days": item?.total_leave_days || calculateDaysDifference(item?.start_date, item?.end_date),
          Status: (
            <span
              className={`badge ${item.status === "Approved"
                ? "bg-success"
                : item.status === "Pending"
                  ? "bg-warning text-dark"
                  : "bg-danger"
                }`}
            >
              {item.status === "Approved" ? "Approved" : item.status === "Pending" ? "Pending" : "Rejected" || "N/A"}
            </span>
          ),
          id: item.id,
          employeeId: item.employee.id,
          start_date: item?.start_date,
          end_date: item?.end_date,
          DayType: item?.leave_duration
        }));

        setLeaveData(formattedData);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching leave data:", error);
        setLoading(false);
      }
    };

    fetchLeaveData();
  }, [userId, token]);

  const calculateDaysDifference = (startDate, endDate, leaveDuration) => {
    if (!startDate || !endDate || !leaveDuration) return 0;

    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const baseDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

    const durationMap = {
      "Quarter Day": 0.25,
      "Half Day": 0.5,
      "Three-quarters day": 0.75,
      "Full Day": 1
    };

    const multiplier = durationMap[leaveDuration?.value] || 1;
    return baseDays * multiplier;
  };

  const handleRowClick = (item) => {
    navigate(`/user/leaveDetails/${userId}/${item.id}/`);
  };

  const handleModalFieldChange = (name, value) => {
    setModalValues(prev => {
      const newValues = { ...prev, [name]: value };

      // Calculate days difference when any relevant field changes
      if (name === 'start_date' || name === 'end_date' || name === 'leave_duration') {
        const daysDiff = calculateDaysDifference(
          newValues.start_date,
          newValues.end_date,
          newValues.leave_duration
        );
        newValues.days_difference = daysDiff;

        if (daysDiff > balanceLeave && name === 'end_date') {
          toast.current.show({
            severity: 'warn',
            summary: 'Warning',
            detail: 'Leave days exceed balance leave!',
            life: 3000,
          });
        }
      }

      return newValues;
    });
  };

  const handleSubmit = async (data) => {
    try {
      const daysDiff = calculateDaysDifference(
        data.start_date,
        data.end_date,
        data.leave_duration
      );

      const totalDays = daysDiff;

      if (totalDays > balanceLeave && data.leave_type?.value === "Casual Leave") {
        toast.current.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Casual Leave not available: Exceeds balance leave!',
          life: 3000,
        });
        return;
      }

      const leaveData = {
        leave_duration: data.leave_duration?.value,
        leave_type: data.leave_type?.value,
        end_date: data.end_date,
        reason: data.reason,
        start_date: data.start_date,
        total_leave_days: totalDays.toString(),
        emp_id: userId,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API}/api/leavemanagement/leaves/`,
        leaveData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      console.log("Leave request submitted successfully:", response.data);
      toast.current.show({
        severity: 'success',
        summary: 'Success',
        detail: "Leave Applied",
        life: 3000,
      });

      // Only reset on success
      setModalValues({
        leave_duration: null,
        leave_type: null,
        start_date: '',
        end_date: '',
        reason: '',
        days_difference: 0
      });

      // Programmatically close the modal (Bootstrap method)
      const modalElement = document.getElementById('staticBackdrop');
      if (modalElement) {
        window.bootstrap.Modal.getInstance(modalElement).hide();
      }

    } catch (error) {
      console.error("Error submitting leave request:", error);
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: "Error submitting, please try again!",
        life: 3000,
      });
      // Modal stays open on error
    }
  };

  const modalFieldsConfig = [
    {
      name: "balance_leave",
      label: "Balance Leave",
      type: "number",
      disabled: true,
      readOnly: true,
      value: balanceLeave,
    },
    {
      name: "leave_duration",
      label: "Leave Duration",
      type: "select",
      options: [
        { value: "Quarter Day", label: "Quarter Day" },
        { value: "Half Day", label: "Half Day" },
        { value: "Three-quarters day", label: "3 Quarter Day" },
        { value: "Full Day", label: "Full Day" },
      ],
      required: true,
      onChange: (value) => handleModalFieldChange("leave_duration", value),
      value: modalValues.leave_duration
    },
    {
      name: "leave_type",
      label: "Leave Type",
      type: "select",
      options: [
        { value: "Work From Home", label: "Work From Home" },
        { value: "Casual Leave", label: "Casual Leave" },
        { value: "Sick Leave", label: "Sick Leave" },
        { value: "Loss of Pay", label: "Loss of Pay" },
      ],
      required: true,
      onChange: (value) => handleModalFieldChange("leave_type", value),
      value: modalValues.leave_type
    },
    {
      name: "start_date",
      label: "Start Date",
      type: "date",
      required: true,
      onChange: (value) => handleModalFieldChange("start_date", value),
      value: modalValues.start_date
    },
    {
      name: "end_date",
      label: "End Date",
      type: "date",
      required: true,
      onChange: (value) => handleModalFieldChange("end_date", value),
      value: modalValues.end_date
    },
    // {
    //   name: "days_difference",
    //   label: "Total Leave Days",
    //   type: "number",
    //   disabled: true,
    //   readOnly: true,
    //   value: modalValues.days_difference
    // },
    {
      name: "reason",
      label: "Leave Reason",
      type: "textarea",
      required: false,
      onChange: (value) => handleModalFieldChange("reason", value),
      value: modalValues.reason
    },
  ];

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="selfLeaveContainer w-100">
      <Toast ref={toast} position="bottom-right" />
      <div className="container p-3 p-md-4 d-flex flex-column gap-3 gap-md-4">
        <h3>My Leave's</h3>
        <button
          className="btn btn-success d-flex gap-3 align-items-center text-nowrap"
          data-bs-toggle="modal"
          data-bs-target="#staticBackdrop"
          style={{ width: "min-content", fontSize: "clamp(12px, 1.4vw, 16px)" }}
        >
          Apply New Leave <i className="bi bi-plus-circle"></i>
        </button>
        {leaveData.length === 0 ? (
          <div
            className="d-flex justify-content-center align-items-end position-relative"
            style={{ height: "50vh" }}
          >
            <div
              className="position-absolute top-0 start-0 w-100 h-100"
              style={{
                backgroundImage: "url(/assets/file.png)",
                backgroundSize: "contain",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                opacity: 0.3,
              }}
            ></div>
            <p className="m-0 fw-light fs-5 position-relative">No Leaves!</p>
          </div>
        ) : (
          <Table
            header={header}
            product={leaveData}
            onRowClick={handleRowClick}
            currentPage={currentPage}
            onPageChange={handlePageChange}
          />
        )}
      </div>
      <Modal
        heading="Apply New Leave"
        fields={modalFieldsConfig}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

export default SelfLeaveTable;